import React, { useState } from 'react'
import {
  MagnifyingGlassIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  EyeIcon,
} from '@heroicons/react/24/outline'
import { useListClientProposalsQuery } from '../store/api/clientProposalsApi'
import { formatDateTimeEST } from '../utils/dateUtils'

const PORTAL_BASE_URL = (import.meta as any).env?.VITE_WEB_URL
  || (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:3000'
    : 'https://web.motionode.com')

function parseSharedContent(raw: string): string {
  try {
    const arr: string[] = typeof raw === 'string' ? JSON.parse(raw) : raw
    return arr
      .map((v) => (v === 'sow' ? 'SOW' : v === 'delivery_plan' ? 'Delivery Plan' : v === 'members_gantt' ? 'Members Gantt' : v))
      .join(', ')
  } catch {
    return String(raw || '—')
  }
}

function ClientStatusBadge({ status, message }: { status?: string | null; message?: string | null }) {
  if (!status || status === 'pending') {
    return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">Pending</span>
  }
  if (status === 'approved') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-50 text-green-700 border border-green-200">
        ✓ Approved
      </span>
    )
  }
  if (status === 'changes_requested') {
    return (
      <div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200">
          ↩ Changes requested
        </span>
        {message && (
          <p className="mt-1 text-xs text-gray-500 italic max-w-[160px] truncate" title={message}>
            "{message}"
          </p>
        )}
      </div>
    )
  }
  return <span className="text-xs text-gray-400">{status}</span>
}

const ClientProposals: React.FC = () => {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [copiedUuid, setCopiedUuid] = useState<string | null>(null)

  const { data, isLoading, error } = useListClientProposalsQuery({
    page,
    limit: 50,
    search: search || undefined,
  })

  const handleCopyLink = (uuid: string) => {
    const url = `${PORTAL_BASE_URL}/proposal/${uuid}`
    navigator.clipboard.writeText(url).then(() => {
      setCopiedUuid(uuid)
      setTimeout(() => setCopiedUuid(null), 2000)
    })
  }

  const rows = data?.data ?? []
  const meta = data?.meta

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error loading client proposals. Please try again.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Client Proposals</h1>
        <p className="text-gray-600">
          Proposal portals shared with clients via magic link
        </p>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        {/* Search */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by client name, email or project…"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPage(1)
                }}
              />
            </div>
          </div>
        </div>

        {/* Stats strip */}
        {meta && (
          <p className="text-xs text-gray-500 mb-4">
            {meta.total} proposal{meta.total !== 1 ? 's' : ''} total
          </p>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Content</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client response</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shared by</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-10 text-center text-gray-500">
                    No client proposals found.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    {/* Client */}
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {row.client_name || <span className="text-gray-400 italic">no name</span>}
                      </div>
                      {row.client_email && <div className="text-xs text-gray-500">{row.client_email}</div>}
                    </td>

                    {/* Project */}
                    <td className="px-4 py-4 text-sm text-gray-900 max-w-xs truncate" title={row.project_name ?? undefined}>
                      {row.project_name || '—'}
                    </td>

                    {/* Content type */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
                        {parseSharedContent(row.shared_content)}
                      </span>
                    </td>

                    {/* Views */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm text-gray-700">
                        <EyeIcon className="h-4 w-4 text-gray-400" />
                        {row.view_count}
                      </div>
                      {row.last_viewed_at && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          last {formatDateTimeEST(row.last_viewed_at)}
                        </div>
                      )}
                    </td>

                    {/* Client response */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <ClientStatusBadge status={row.client_status} message={row.client_message} />
                    </td>

                    {/* Shared by */}
                    <td className="px-4 py-4 text-xs text-gray-500">
                      {row.created_by_email || '—'}
                    </td>

                    {/* Created */}
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDateTimeEST(row.created_at)}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleCopyLink(row.uuid)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
                          title="Copy portal link"
                        >
                          {copiedUuid === row.uuid ? (
                            <><CheckIcon className="h-3.5 w-3.5" />Copied</>
                          ) : (
                            <><ClipboardDocumentIcon className="h-3.5 w-3.5" />Copy link</>
                          )}
                        </button>
                        <a
                          href={`${PORTAL_BASE_URL}/proposal/${row.uuid}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                          title="Open portal"
                        >
                          Open ↗
                        </a>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {meta && meta.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
            <p className="text-sm text-gray-700">
              Showing {(meta.page - 1) * meta.limit + 1}–
              {Math.min(meta.page * meta.limit, meta.total)} of {meta.total}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={meta.page <= 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
                disabled={meta.page >= meta.totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ClientProposals
